import { useEffect, useMemo, useState } from 'react';
import { RotateCcw, Save, Search, Shirt, ShieldCheck } from 'lucide-react';
import { roles } from '../lib/library';
import { colours, garments } from '../lib/trustLibrary';
import { loadGenericUniforms, saveGenericUniforms } from '../lib/genericUniformApi';
import UniformPreview from './UniformPreview';

const normaliseGarmentId = (garmentId) => {
  if (garmentId === 'no_uniform') return 'no_uniform';
  if (garmentId === 'healthcare_tunic') return 'healthcare_tunic';
  // Historical `tunic` meant the combined "Scrubs / tunic" option. Keep those
  // records rendering as the existing scrub top until explicitly changed.
  return 'scrubs';
};

function defaultUniform(role) {
  return {
    colourId: role?.presentation?.defaultUniformId || 'no_uniform',
    garmentId: (role?.presentation?.defaultUniformId || 'no_uniform') === 'no_uniform' ? 'no_uniform' : 'scrubs',
    note: '',
  };
}

function fallbackColourFor(role) {
  const canonical = defaultUniform(role).colourId;
  if (canonical !== 'no_uniform') return canonical;
  return colours.find((item) => item.id !== 'no_uniform')?.id || 'grey';
}

export default function GenericUniformsStudio({ workspaceSwitcher = null, studioAccess = null }) {
  const platformAdmin = studioAccess?.roles?.includes('platform_admin');
  const [query, setQuery] = useState('');
  const [uniforms, setUniforms] = useState({});
  const [status, setStatus] = useState('loading');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!platformAdmin) return;
    let cancelled = false;
    loadGenericUniforms().then((payload) => {
      if (cancelled) return;
      setUniforms(payload.uniforms || {});
      setStatus('ready');
    }).catch((error) => {
      if (cancelled) return;
      setStatus('error');
      setNotice(error.message || 'Generic uniforms could not be loaded.');
    });
    return () => { cancelled = true; };
  }, [platformAdmin]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return roles;
    return roles.filter((role) => `${role.title} ${role.roleKey} ${role.categoryLabel}`.toLowerCase().includes(needle));
  }, [query]);

  if (!platformAdmin) return <main className="access-state"><ShieldCheck size={34}/><h1>Platform Admin only</h1><p>Generic NHS defaults can only be changed by a platform administrator.</p></main>;

  const effective = (role) => {
    const raw = uniforms[role.roleKey] || defaultUniform(role);
    return { ...raw, garmentId: normaliseGarmentId(raw.garmentId) };
  };
  const change = (role, patch) => setUniforms((current) => {
    const next = { ...effective(role), ...patch };
    if (patch.garmentId === 'no_uniform') {
      next.colourId = 'no_uniform';
    } else if (patch.garmentId && next.garmentId !== 'no_uniform' && next.colourId === 'no_uniform') {
      next.colourId = fallbackColourFor(role);
    }
    if (next.garmentId === 'no_uniform') next.colourId = 'no_uniform';
    return { ...current, [role.roleKey]: next };
  });
  const reset = (role) => setUniforms((current) => {
    const next = { ...current };
    delete next[role.roleKey];
    return next;
  });
  const save = async () => {
    setStatus('saving'); setNotice('');
    try {
      const payload = await saveGenericUniforms(uniforms);
      setUniforms(payload.uniforms || {});
      setStatus('ready');
      setNotice('Generic NHS uniform defaults saved. Trust-specific published uniforms remain unchanged.');
    } catch (error) {
      setStatus('error');
      setNotice(error.message || 'Generic uniforms could not be saved.');
    }
  };

  return <div className="studio-shell">
    <aside className="studio-sidebar">{workspaceSwitcher}</aside>
    <main className="studio-main">
      <header className="studio-page-header"><div><p className="eyebrow">Platform configuration</p><h1>Generic NHS uniforms</h1><p>Manage the default uniform shown when no Trust-specific approved uniform is available. Trust-specific configuration always takes precedence.</p></div><Shirt size={28}/></header>
      <div className="workflow-lock-banner"><ShieldCheck/><div><strong>Safe inheritance</strong><span>These are central fallbacks only. Saving here does not rewrite or republish any Trust configuration.</span></div></div>
      <div className="role-toolbar"><label className="search-field"><Search size={16}/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search roles"/></label><button className="button primary" type="button" onClick={save} disabled={status === 'saving'}><Save size={16}/>{status === 'saving' ? 'Saving…' : 'Save generic defaults'}</button></div>
      {notice ? <p role="status">{notice}</p> : null}
      <div className="trust-card-list">
        {filtered.map((role) => {
          const value = effective(role);
          const fixedParamedic = role.roleKey === 'paramedic';
          const isProfessionalDress = value.garmentId === 'no_uniform';
          const colour = colours.find((item) => item.id === (isProfessionalDress ? 'no_uniform' : value.colourId));
          return <section className="trust-config-panel" key={role.roleKey}>
            <div className="trust-panel-heading"><div><p className="eyebrow">{role.categoryLabel}</p><h2>{role.title}</h2><small>{role.roleKey}</small></div><UniformPreview body={colour?.body || '#6B7280'} trim={colour?.trim || '#374151'} garment={value.garmentId} fixedIllustration={fixedParamedic ? 'paramedic' : null}/></div>
            <div className="trust-config-grid"><div className="config-fields">
              {fixedParamedic ? <div className="workflow-lock-banner"><ShieldCheck/><div><strong>Fixed platform illustration</strong><span>The Paramedic image is centrally fixed and cannot be replaced by a generic or Trust-specific uniform.</span></div></div> : <>
              <label>Uniform type<select value={value.garmentId} onChange={(event) => change(role, { garmentId: event.target.value })}>{garments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>Uniform colour<select value={isProfessionalDress ? 'no_uniform' : (value.colourId || 'no_uniform')} disabled={isProfessionalDress} onChange={(event) => change(role, { colourId: event.target.value })}>{colours.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>{isProfessionalDress ? <small>Not applicable when no uniform / professional dress is selected.</small> : null}</label>
              <label>Generic note<textarea rows="2" value={value.note || ''} onChange={(event) => change(role, { note: event.target.value })} placeholder="Optional patient-facing note about variation"/></label>
              </>}
            </div><div className="uniform-card-preview"><strong>Effective generic default</strong><span>{fixedParamedic ? 'Paramedic uniform · fixed platform illustration' : (colour?.name || 'No standard uniform')}</span>{!fixedParamedic && value.note ? <small>{value.note}</small> : null}{!fixedParamedic ? <button className="button secondary" type="button" onClick={() => reset(role)}><RotateCcw size={15}/>Reset to canonical default</button> : null}</div></div>
          </section>;
        })}
      </div>
    </main>
  </div>;
}
